// Dashboard.js - Updated with header package status integration
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc,
  deleteDoc, 
  doc,
  writeBatch 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { generateMultipleDemoTeams } from '../utils/demoDataGenerator';
import { calculateCompletionStats } from '../utils/dashboardCalculations';

// ADDED: Import helper functions from teamDataProcessor (like TeamDashboard.js)
import { 
  getTeamStatistics 
} from '../utils/teamDataProcessor';

// ADDED: Import package management functions
import { 
  getPackageStatus 
} from '../utils/packageManagement';

import './Dashboard.css';

const Dashboard = () => {
  const { user, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  
  // Existing state
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // New demo generator state
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [demoProgress, setDemoProgress] = useState(null);

  // ADDED: Package status state
  const [packageStatus, setPackageStatus] = useState(null);
  const [packageLoading, setPackageLoading] = useState(true);

  // Check if development environment
  const [profielCompleet, setProfielCompleet] = useState(true);
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isTestUser = user?.email === 'test@tractum.nl';
  const showDemoFeatures = isDevelopment && isTestUser;

  // Get organization ID helper
  const getOrganizationId = () => {
    return userProfile?.organizationId || 'default_org';
  };

  // ADDED: Logout handler
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Existing useEffect for loading teams
  useEffect(() => {
    if (user && userProfile) {
  loadTeams();
  loadPackageStatus();
  checkProfielCompleet();
}
  }, [user, userProfile]);

  // ADDED: Load package status function
  const loadPackageStatus = async () => {
    if (!user?.email) return;
    
    try {
      setPackageLoading(true);
      const status = await getPackageStatus(user.email);
      setPackageStatus(status);
      console.log('📦 Package status loaded:', status);
    } catch (error) {
      console.error('Error loading package status:', error);
      setPackageStatus({
        hasPackage: false,
        message: 'Fout bij laden package status'
      });
    } finally {
      setPackageLoading(false);
    }
  };

const checkProfielCompleet = async () => {
  try {
    const profileDoc = await getDoc(doc(db, 'userProfiles', user.uid));
    setProfielCompleet(profileDoc.exists() && profileDoc.data()?.naam);
  } catch (error) {
    console.error('Fout bij controleren profiel:', error);
  }
};

  // UPDATED: loadTeams function using TeamDashboard.js pattern with helper
  const loadTeams = async () => {
    try {
      setLoading(true);
      const teamsQuery = query(
        collection(db, 'teams'),
        // where('organizationId', '==', getOrganizationId()) - Niet gebruiken ivm privacy
        where('createdByEmail', '==', user.email) // ADDED - show only user's teams
      );
      
      const querySnapshot = await getDocs(teamsQuery);
      console.log('🔍 Query found teams:', querySnapshot.size); // debug 

// DEBUG: Check elk team
querySnapshot.forEach(doc => {
  const data = doc.data();
  console.log('🔍 Team found:', {
    name: data.name,
    createdByEmail: data.createdByEmail, 
    currentUser: user.email,
    match: data.createdByEmail === user.email
  });
});
      const teamsData = [];
      
      // COPIED FROM TeamDashboard.js: Process each team with helper logic
      for (const teamDoc of querySnapshot.docs) {
        const teamData = {
          id: teamDoc.id,
          ...teamDoc.data()
        };
        
        // COPIED FROM TeamDashboard.js: Fetch invitations
        const invitationsRef = collection(db, 'teams', teamDoc.id, 'invites');
        const invitationsSnapshot = await getDocs(invitationsRef);
        const invitationsData = invitationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // COPIED FROM TeamDashboard.js: Fetch responses
        const responsesRef = collection(db, 'responses');
        const responsesQuery = query(responsesRef, where('teamId', '==', teamDoc.id));
        const responsesSnapshot = await getDocs(responsesQuery);
        const responsesData = responsesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // ADDED: Use helper function for consistent stats calculation (like TeamDashboard.js)
        const stats = getTeamStatistics(responsesData, invitationsData);
        
        // SIMPLIFIED: Only ja/nee logic for completion
        teamData.responseCount = stats.responseCount;
        teamData.invitationCount = stats.invitationCount;
        teamData.hasResponses = stats.hasResponses;
        teamData.isComplete = stats.isComplete; // Helper determines ja/nee
        
        console.log(`Team: ${teamData.name} | Invites: ${stats.invitationCount} | Responses: ${stats.responseCount} | Complete: ${stats.isComplete ? 'JA' : 'NEE'}`);
        
        teamsData.push(teamData);
      }
      
      setTeams(teamsData);
      console.log('✅ Teams loaded with helper-based response data:', teamsData.length);
    } catch (error) {
      console.error('Error loading teams:', error);
    } finally {
      setLoading(false);
    }
  };

  // ADDED: Package status component
  const renderPackageStatus = () => {
    if (packageLoading) {
      return (
        <div className="package-status-header">
          <div className="package-status-loading">
            <div className="package-spinner"></div>
            <span>Laden...</span>
          </div>
        </div>
      );
    }

    if (!packageStatus?.hasPackage) {
      return (
        <div className="package-status-header">
          <div className="package-status-inactive">
            <span className="package-icon">📦</span>
            <div className="package-text">
              <span className="package-title">Geen Package</span>
              {/*<span className="package-subtitle">Contact admin</span>*/}
            </div>
          </div>
        </div>
      );
    }

    // Determine status styling
    let statusClass = 'package-status-good';
    if (packageStatus.isExpired || packageStatus.scansLeft <= 0) {
      statusClass = 'package-status-error';
    } else if (packageStatus.scansLeft <= 1 || packageStatus.daysLeft <= 7) {
      statusClass = 'package-status-warning';
    }

    return (
      <div className="package-status-header">
        <div className={`package-status-active ${statusClass}`}>
          <span className="package-icon">📦</span>
          <div className="package-text">
            <span className="package-title">
              {packageStatus.scansLeft}/{packageStatus.scansTotal} scans
            </span>
            <span className="package-subtitle">
              {packageStatus.daysLeft} dagen over
            </span>
          </div>
          {(packageStatus.isExpired || packageStatus.scansLeft <= 0) && (
            <span className="package-alert">❌</span>
          )}
          {(packageStatus.scansLeft <= 1 || packageStatus.daysLeft <= 7) && 
           !packageStatus.isExpired && packageStatus.scansLeft > 0 && (
            <span className="package-alert">⚠️</span>
          )}
        </div>
      </div>
    );
  };

  // Existing delete functionality
  const toggleTeamSelection = (teamId) => {
    setSelectedTeams(prev => 
      prev.includes(teamId) 
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const deleteSelectedTeams = async () => {
    if (selectedTeams.length === 0) return;
    
    setIsDeleting(true);
    try {
      const batch = writeBatch(db);
      
      for (const teamId of selectedTeams) {
        // Delete team document
        const teamRef = doc(db, 'teams', teamId);
        batch.delete(teamRef);
        
        // Note: In production, you'd also delete related data (invites, responses)
      }
      
      await batch.commit();
      
      // Refresh teams list
      await loadTeams();
      setSelectedTeams([]);
      setShowDeleteModal(false);
      
      console.log(`✅ Deleted ${selectedTeams.length} teams`);
    } catch (error) {
      console.error('Error deleting teams:', error);
      alert('Er is een fout opgetreden bij het verwijderen van relaties.');
    } finally {
      setIsDeleting(false);
    }
  };

  // NEW: Demo team generation functionality
  const handleDemoProgress = (progressData) => {
    setDemoProgress(progressData);
  };

  const generateDemoTeams = async (preset) => {
    setIsGenerating(true);
    setDemoProgress(null);
    
    try {
      console.log(`🎲 Generating ${preset.count} demo teams...`);
      
      const options = {
        teamCount: preset.count,
        withResponses: true,
        onProgress: handleDemoProgress
      };

      const result = await generateMultipleDemoTeams(
        user.email,
        getOrganizationId(),
        options
      );

      console.log('✅ Demo teams generated:', result);
      
      // Refresh teams list to show new teams
      await loadTeams();
      
      // Close modal and show success
      setShowDemoModal(false);
      setDemoProgress({
        phase: 'success',
        message: `${result.teamsCreated} demo teams aangemaakt!`
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setDemoProgress(null);
      }, 3000);

    } catch (error) {
      console.error('❌ Demo generation failed:', error);
      setDemoProgress({
        phase: 'error',
        message: `Fout: ${error.message}`
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Demo presets for modal
  const demoPresets = [
    { name: 'Klein', count: 3, description: '3 teams (5-8 leden)' },
    { name: 'Medium', count: 5, description: '5 teams (8-15 leden)' },
    { name: 'Groot', count: 10, description: '10 teams (10-20 leden)' }
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p className="loading-text">Teams laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-wrapper">
        {!profielCompleet && (
  <div className="profile-warning">
    ⚠️ Je profiel is nog niet ingevuld. Rapporten bevatten dan geen contactgegevens.{' '}
    <span
      className="profile-warning-link"
      onClick={() => navigate('/profiel')}
    >
      Profiel invullen →
    </span>
  </div>
)}
        {/* UPDATED: Header with Package Status and Logout Button */}
        <div className="dashboard-header">
          <div className="header-content">
            <h1>📊 Therapeut Dashboard</h1>
            <p>Welkom {userProfile?.name || user?.email} • {teams.length} relaties</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Package Status */}
            {renderPackageStatus()}
            {/* ADDED: Logout Button */}
            <button 
              className="logout-button"
              onClick={handleLogout}
            >
              Uitloggen
            </button>
          </div>
        </div>

        {/* Demo Progress Display */}
        {demoProgress && (
          <div className={`demo-progress-message ${demoProgress.phase}`}>
            {demoProgress.phase === 'error' ? '❌' : 
             demoProgress.phase === 'success' ? '✅' : '⏳'} {demoProgress.message}
          </div>
        )}

        {/* Main Content */}
        {teams.length === 0 ? (
          <div className="new-team-section">
            <h2>🎯 Welkom bij de GezinsScan!</h2>
            <p>Je hebt nog geen relaties aangemaakt. Maak je eerste relatie aan om te beginnen.</p>
            
            <div className="action-buttons">
              <button 
                className="create-team-button"
                onClick={() => navigate('/create-team')}
              >
                ➕ Maak je eerste relatie
              </button>

              {/* Development-only Demo Features */}
              {showDemoFeatures && (
                <button 
                  className="demo-button"
                  onClick={() => setShowDemoModal(true)}
                  disabled={isGenerating}
                >
                  🎲 Genereer demo teams voor testing
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Action Buttons for existing teams */}
            <div className="new-team-section">
              <div className="action-buttons">
                <button 
                  className="create-team-button"
                  onClick={() => navigate('/create-team')}
                >
                  ➕ Nieuwe Relatie
                </button>

                {/* Development-only Demo Features */}
                {showDemoFeatures && (
                  <>
                    <button 
                      className="demo-button"
                      onClick={() => setShowDemoModal(true)}
                      disabled={isGenerating}
                    >
                      🎲 Maak Test Teams
                    </button>
                    
                    <button 
                      className="delete-test-button"
                      onClick={() => setShowDeleteModal(true)}
                    >
                      🗑️¸ Verwijder Test Teams
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* UPDATED: Teams Grid with helper-based completion logic */}
            <div className="teams-section">
              <h3>Jouw Relaties</h3>
              <div className="teams-grid">
                {teams.map(team => {
                  // Format creation date
                  const createdDate = team.createdAt?.toDate ? 
                    team.createdAt.toDate().toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'numeric', 
                      year: 'numeric'
                    }) : 
                    '17-8-2025'; // Fallback for demo teams

                  // FIXED: Use helper-calculated completion status (JA/NEE).
                  const isComplete = team.isComplete; // Helper determines this
                  
                  console.log(`Rendering team: ${team.name} | Helper says Complete: ${isComplete ? 'JA' : 'NEE'} | Invites: ${team.invitationCount} | Responses: ${team.responseCount}`);
                  
                  return (
                    <div key={team.id} className="team-card">
                      <div className="team-card-header">
                        <h4 className="team-card-title">
                          {team.name}
                        </h4>
                        <p className="team-card-date">
                          Aangemaakt: {createdDate}
                        </p>
                        <div className={`team-card-status ${isComplete ? 'complete' : 'pending'}`}>
                          ● {isComplete ? 'Alle scans ingevuld' : 'Nog niet alle scans ingevuld'}
                        </div>
                      </div>
                      
                      <button 
                        className="team-manage-button"
                        onClick={() => navigate(`/dashboard/${team.id}`)}
                      >
                        {isComplete ? 'Bekijk Resultaten' : 'Relatie Beheren'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Demo Generation Modal */}
        {showDemoModal && (
          <div className="demo-modal-overlay" onClick={() => setShowDemoModal(false)}>
            <div className="demo-modal-content" onClick={e => e.stopPropagation()}>
              <div className="demo-modal-header">
                <h3>🎲 Demo Teams Genereren</h3>
                <button 
                  className="demo-modal-close"
                  onClick={() => setShowDemoModal(false)}
                >
                  ➕
                </button>
              </div>
              
              <div className="demo-modal-body">
                <p>Kies hoeveel demo teams je wilt aanmaken:</p>
                
                <div className="demo-preset-options">
                  {demoPresets.map(preset => (
                    <button
                      key={preset.name}
                      className="demo-preset-button"
                      onClick={() => generateDemoTeams(preset)}
                      disabled={isGenerating}
                    >
                      <div className="demo-preset-title">{preset.name}</div>
                      <div className="demo-preset-description">{preset.description}</div>
                    </button>
                  ))}
                </div>

                {isGenerating && demoProgress && (
                  <div className="demo-progress-container">
                    <div className="demo-progress-bar">
                      <div 
                        className="demo-progress-fill"
                        style={{ 
                          width: demoProgress.current && demoProgress.total 
                            ? `${(demoProgress.current / demoProgress.total) * 100}%`
                            : '0%'
                        }}
                      ></div>
                    </div>
                    <p className="demo-progress-text">{demoProgress.message}</p>
                  </div>
                )}

                <div className="demo-modal-note">
                  <small>⚠️ Demo teams bevatten automatisch ingevulde quiz responses voor testing.</small>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && (
          <div className="delete-modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="delete-modal-content" onClick={e => e.stopPropagation()}>
              <div className="delete-modal-header">
                <h3>🗑️¸ Teams Verwijderen</h3>
                <button 
                  className="delete-modal-close"
                  onClick={() => setShowDeleteModal(false)}
                >
                  ➕
                </button>
              </div>
              
              <div className="delete-modal-body">
                <p>Selecteer teams om te verwijderen:</p>
                
                <div className="teams-selection">
                  {teams.map(team => (
                    <label key={team.id} className="team-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedTeams.includes(team.id)}
                        onChange={() => toggleTeamSelection(team.id)}
                      />
                      <span className="team-checkbox-label">{team.name}</span>
                    </label>
                  ))}
                </div>

                {selectedTeams.length > 0 && (
                  <div className="delete-actions">
                    <button
                      className="delete-confirm-button"
                      onClick={deleteSelectedTeams}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Verwijderen...' : `Verwijder ${selectedTeams.length} teams`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Team Comparison Section (if 2+ teams) */}
        {teams.length >= 2 && (
          <div className="new-team-section">
            <div className="action-buttons">
              <button
                className="create-team-button"
                onClick={() => navigate('/team-comparison')}
              >
                🔄 Vergelijk Relaties
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;